// SIMS-6263 (PRD v0.3 FR-11) — a digital fishbowl's prize tier probabilities must total
// exactly 100%. This is the FORM's copy of a rule that is also enforced server-side
// (cmi-backend-lambdas-sims-application-02/libs/shared/src/helpers/fishbowl-prize-distribution.helper.ts).
//
// Both exist deliberately, and neither is redundant:
//   - the server rule is the one that HOLDS: FR-11 requires it "however the configuration is
//     submitted", and the Treatment Config assistant's schema deliberately does not model
//     rewardCustomConfig, so a form-only rule is bypassed by the assistant and by the API;
//   - the form rule is what the configurator SEES: AC1 requires a live total that updates as
//     the probabilities are edited, which a server round trip cannot provide.
// The two use the same wording so an administrator never sees the rule stated two ways.
//
// Running example: A $0.00/50%, B $5.00/35%, C $12.75/15% -> 100%, saves.
//                  A 30 / B 30 / C 20 -> 80%, blocked.

/**
 * Probabilities are totalled in integer BASIS POINTS, not as floats.
 *
 * Seven tiers at 12.1% plus one at 15.3% is exactly 100%, but summing those IEEE-754 doubles
 * gives 99.99999999999999 — a float comparison against 100 would reject a configuration the
 * configurator typed correctly. The comparison must still be EXACT (99.9% and 100.5% are both
 * rejected), so a tolerance is not an option; integers are.
 */
const REQUIRED_BASIS_POINTS = 10_000;

interface PrizeTierLike {
	amount?: number;
	probability?: number;
	displayMessage?: string;
}

interface FishbowlConfigLike {
	name?: string;
	rewardCustomConfig?: {
		fishbowlType?: string;
		prizeDistributions?: PrizeTierLike[];
	} | null;
	additionalTreatmentConfigs?: FishbowlConfigLike[] | null;
}

/** 9990 -> "99.9", 10000 -> "100", 8000 -> "80". Trailing zeros are dropped. */
export function formatProbabilityTotal(basisPoints: number): string {
	return String(Number((basisPoints / 100).toFixed(2)));
}

/**
 * Total of a tier list, in basis points. A non-numeric probability contributes 0 rather than
 * poisoning the sum with NaN — the readout must keep showing a usable number while the
 * configurator is mid-edit (an empty field is momentarily not a number).
 */
export function prizeProbabilityBasisPoints(tiers?: PrizeTierLike[]): number {
	if (!Array.isArray(tiers)) {
		return 0;
	}
	return tiers.reduce((sum, tier) => {
		const probability = Number(tier?.probability);
		return (
			sum + (Number.isFinite(probability) ? Math.round(probability * 100) : 0)
		);
	}, 0);
}

/** True when the tier list totals exactly 100%. An empty list is NOT a valid total. */
export function isPrizeProbabilityTotalValid(tiers?: PrizeTierLike[]): boolean {
	return prizeProbabilityBasisPoints(tiers) === REQUIRED_BASIS_POINTS;
}

/**
 * The label shown beneath the prize tiers — AC1's "live error chip showing the current total".
 * Valid: "Total: 100%".  Invalid: "Total: 80% — must equal 100%".
 *
 * Names no currency: SIMS-6215 will label TOKEN amounts "pts" rather than "$", and this copy
 * has to read correctly there too.
 */
export function probabilityTotalLabel(tiers?: PrizeTierLike[]): string {
	const total = formatProbabilityTotal(prizeProbabilityBasisPoints(tiers));
	return isPrizeProbabilityTotalValid(tiers)
		? `Total: ${total}%`
		: `Total: ${total}% — must equal 100%`;
}

/**
 * Gate for the save path: the first offending configuration across every template and its
 * additional configs, or null when all are valid.
 *
 * Mirrors the server helper, including the deliberate skip of an EMPTY tier list — "not
 * configured yet" is not "configured wrongly", no AC covers it, and rejecting it would block
 * saves of existing digital configurations that carry no tiers (the unmeasured population OQ-1
 * is about).
 */
export function fishbowlProbabilityErrorMessage(
	configs?: FishbowlConfigLike[] | null,
): string | null {
	if (!Array.isArray(configs)) {
		return null;
	}

	const flattened: FishbowlConfigLike[] = [];
	configs.forEach((config) => {
		if (!config) {
			return;
		}
		flattened.push(config);
		if (Array.isArray(config.additionalTreatmentConfigs)) {
			config.additionalTreatmentConfigs.forEach((additional) => {
				if (additional) {
					flattened.push(additional);
				}
			});
		}
	});

	for (const config of flattened) {
		const reward = config.rewardCustomConfig;
		if (!reward || reward.fishbowlType !== "DIGITAL") {
			continue;
		}
		const tiers = reward.prizeDistributions;
		if (!Array.isArray(tiers) || tiers.length === 0) {
			continue;
		}
		if (isPrizeProbabilityTotalValid(tiers)) {
			continue;
		}
		const total = formatProbabilityTotal(prizeProbabilityBasisPoints(tiers));
		// Naming the configuration matters: a plan carries several activities, and an
		// unqualified message leaves the administrator hunting for which one is wrong.
		const label = config.name?.trim();
		return label
			? `Prize tier probabilities must total 100% in "${label}". That configuration totals ${total}%.`
			: `Prize tier probabilities must total 100%. This configuration totals ${total}%.`;
	}

	return null;
}
